import { type ReactElement, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { config } from '@/config';
import {
  useCreateUploadToken,
  useRevokeUploadToken,
  useTestUploadToken,
  useUploadTokensQuery,
} from '@/features/uploads';
import { useNotifications } from '@/providers/notificationHooks';
import { getTargetActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getToastMessage } from '@/shared/copy/toasts';
import { useMutationErrorSnackbar } from '@/shared/hooks/useMutationErrorSnackbar';
import { componentSizes } from '@/theme/componentSizes';
import { r, s, typographyTokens } from '@/theme/tokens';
import {
  Box,
  CodeOutputBlock,
  DialogLayout,
  formatDate,
  Panel,
  Stack,
  StatusBanner,
  Text,
} from '@/ui';
import { settingsCopyTextSx, settingsValueTextSx } from '@/ui/composites/settingsPageTypographySx';
import { ButtonGroup } from '@/ui/primitives';
import { CopyIcon, DeleteIcon, Link, RotateRight, Tooltip } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { PlayArrowIcon } from '@/ui/primitives/icons';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import { TextField } from '@/ui/primitives/TextField';
import { resolveTokenStatus } from './uploadTokens/tokenUtils';

type ProjectUploadTokensSectionProps = {
  projectId: string;
  disabled?: boolean;
  disabledReason?: string;
  disabledHref?: string;
};
type ProjectUploadTokensContainer = 'panel' | 'none';
type UploadSnippetFormat = 'XLSX' | 'CSV' | 'XML' | 'JSON';
const maskedUploadTokenValue = '•••••••••••••••••••••••••••••••••••••••••••';
const actionGroupBackgroundColor = 'var(--surface-page)';

export const ProjectUploadTokenMetadataPanel = ({
  projectId,
}: {
  projectId: string;
}): ReactElement => {
  const tokensQuery = useUploadTokensQuery(projectId);
  const tokens = tokensQuery.data ?? [];
  const activeTokens = tokens.filter((token) => resolveTokenStatus(token) === 'Active');
  const latestActiveToken = activeTokens.length > 0 ? activeTokens[0] : null;
  const createdLabel =
    formatDate(latestActiveToken?.createdAt ?? null, { includeTime: true }) || '-';
  const lastUsedLabel =
    formatDate(latestActiveToken?.lastUsedAt ?? null, { includeTime: true }) || '-';

  return (
    <Panel surfaceVariant="settings" sx={{ py: s('md') }}>
      <Stack gap="sm">
        <Box sx={{ minWidth: 0 }}>
          <Stack gap="xxs">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Created
            </Text>
            <Text role="body" sx={settingsValueTextSx}>
              {createdLabel}
            </Text>
          </Stack>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack gap="xxs">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Last Used
            </Text>
            <Text role="body" sx={settingsValueTextSx}>
              {lastUsedLabel}
            </Text>
          </Stack>
        </Box>
      </Stack>
    </Panel>
  );
};

export const ProjectUploadTokensSection = ({
  projectId,
  disabled = false,
  disabledReason = 'Upload token management is not available on Free plans.',
  disabledHref = '/plan-settings',
  container = 'panel',
  initialSnippetTab = 'XLSX',
}: ProjectUploadTokensSectionProps & {
  container?: ProjectUploadTokensContainer;
  initialSnippetTab?: UploadSnippetFormat;
}): ReactElement => {
  const { notify } = useNotifications();
  const mutationError = useMutationErrorSnackbar();
  const tokensQuery = useUploadTokensQuery(projectId);
  const createToken = useCreateUploadToken();
  const revokeToken = useRevokeUploadToken();
  const testToken = useTestUploadToken();
  const [tokenValue, setTokenValue] = useState('');
  const [activeSnippetTab, setActiveSnippetTab] = useState<UploadSnippetFormat>(initialSnippetTab);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [snippetsOpen, setSnippetsOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const uploadEndpoint = `${config.apiBaseUrl.replace(/\/+$/, '')}/uploads`;
  const actionIconSize = componentSizes.auth.inputHeight - 2;
  const actionGroupSx = {
    backgroundColor: actionGroupBackgroundColor,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: r('sm'),
    overflow: 'hidden',
    '& .MuiButton-root': {
      color: 'text.primary',
      borderColor: 'divider',
      minWidth: 0,
      backgroundColor: actionGroupBackgroundColor,
      borderRadius: '0 !important',
    },
    '& .MuiButton-root:hover': {
      backgroundColor: 'action.hover',
    },
    '& .MuiButtonGroup-grouped': {
      width: actionIconSize,
      minWidth: actionIconSize,
      maxWidth: actionIconSize,
      flex: `0 0 ${actionIconSize}px`,
      padding: 0,
      borderRadius: '0 !important',
    },
    '& .MuiButtonGroup-grouped:not(:last-of-type)': {
      borderRight: '1px solid',
      borderColor: 'divider',
    },
    '& .MuiButton-root.Mui-disabled': {
      color: 'text.disabled',
    },
  } as const;
  const actionButtonSx = {
    width: actionIconSize,
    height: actionIconSize,
    minWidth: actionIconSize,
    minHeight: actionIconSize,
    padding: 0,
    borderRadius: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    '& .MuiSvgIcon-root': {
      fontSize: 17,
      display: 'block',
    },
  } as const;
  const tokens = tokensQuery.data ?? [];
  const activeTokens = useMemo(
    () => tokens.filter((token) => resolveTokenStatus(token) === 'Active'),
    [tokens],
  );
  const hasPlaintextToken = tokenValue.trim().length > 0;
  const hasConfiguredToken = hasPlaintextToken || activeTokens.length > 0;
  const displayTokenValue = hasPlaintextToken
    ? tokenValue
    : hasConfiguredToken
      ? maskedUploadTokenValue
      : '';
  const savePending = createToken.isPending || revokeToken.isPending;
  const testPending = testToken.isPending;
  const disabledActionLabel = disabled ? disabledReason : null;
  const uploadXmlSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpoint}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/xml" \\',
    '  --header "x-file-name: $XML_FILE_NAME" \\',
    '  --data-binary @"$XML_FILE_PATH"',
  ].join('\n');
  const uploadJsonSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpoint}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/json" \\',
    '  --header "x-file-name: $JSON_FILE_NAME" \\',
    '  --data-binary @"$JSON_FILE_PATH"',
  ].join('\n');
  const uploadCsvSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpoint}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: text/csv" \\',
    '  --header "x-file-name: $CSV_FILE_NAME" \\',
    '  --data-binary @"$CSV_FILE_PATH"',
  ].join('\n');
  const uploadXlsxSnippet = [
    'curl --request POST \\',
    `  "${uploadEndpoint}" \\`,
    '  --header "x-upload-token: $UPLOAD_TOKEN" \\',
    '  --header "content-type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" \\',
    '  --header "x-file-name: $XLSX_FILE_NAME" \\',
    '  --data-binary @"$XLSX_FILE_PATH"',
  ].join('\n');

  const copyUploadToken = (): void => {
    if (!hasPlaintextToken) return;
    void navigator.clipboard.writeText(tokenValue);
    notify({
      message: getToastMessage('clipboard.copied', { label: 'Upload token' }),
      tone: 'green',
    });
  };

  const copyUploadSnippet = (snippet: string, format: UploadSnippetFormat): void => {
    void navigator.clipboard.writeText(snippet);
    notify({
      message: getToastMessage('upload.snippetCopied', { format }),
      tone: 'green',
    });
  };

  const applyGenerateToken = async (): Promise<void> => {
    if (disabled) return;
    try {
      for (const token of activeTokens) {
        await revokeToken.mutateAsync({ projectId, tokenId: token.id });
      }
      const created = await createToken.mutateAsync({
        projectId,
        input: { name: `ci-upload-token-${Date.now()}` },
      });
      setTokenValue(created.token);
      setBannerDismissed(false);
      mutationError.clearError();
      setRegenerateOpen(false);
      notify({
        message: hasConfiguredToken ? 'Regenerated upload token' : 'Generated upload token',
        tone: 'green',
      });
    } catch (err) {
      mutationError.setError(err);
    }
  };

  const onGenerateOrRegenerate = (): void => {
    if (disabled) return;
    if (hasConfiguredToken) {
      setRegenerateOpen(true);
      return;
    }
    void applyGenerateToken();
  };

  const applyRevoke = async (): Promise<void> => {
    if (disabled) return;
    try {
      for (const token of activeTokens) {
        await revokeToken.mutateAsync({ projectId, tokenId: token.id });
      }
      setTokenValue('');
      setRevokeOpen(false);
      mutationError.clearError();
      notify({
        message: getToastMessage('entity.revoked', { entity: 'upload token' }),
        tone: 'green',
      });
    } catch (err) {
      mutationError.setError(err);
    }
  };

  const onTestUpload = (): void => {
    if (disabled) return;
    if (!hasPlaintextToken) return;
    testToken.mutate(
      { projectId, input: { token: tokenValue } },
      {
        onSuccess: () => {
          mutationError.clearError();
          notify({ message: getToastMessage('uploadToken.testPassed'), tone: 'green' });
        },
        onError: (err) => {
          mutationError.setError(err);
        },
      },
    );
  };

  const content = (
    <Stack gap="md">
      {disabledActionLabel ? (
        <StatusBanner
          tone="orange"
          variant="outlined"
          dense
          sx={{
            px: s('lg'),
            '& .MuiAlert-icon': { marginRight: s('md') },
            '& .MuiAlert-message': { flex: 1 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: s('md'),
              width: '100%',
            }}
          >
            <Text role="body" component="span">
              {disabledActionLabel}
            </Text>
            {disabledHref ? (
              <Link
                href={disabledHref}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ flexShrink: 0, fontWeight: typographyTokens.bodyStrong.fontWeight }}
              >
                Upgrade
              </Link>
            ) : null}
          </Box>
        </StatusBanner>
      ) : null}
      <Stack gap="lg">
        <Stack gap="md">
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: s('md'), width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                fieldStyle="settings"
                value={displayTokenValue}
                placeholder="Generate an upload token"
                slotProps={{ input: { readOnly: true } }}
                sx={{ '& .MuiOutlinedInput-input': { color: 'text.secondary' } }}
                onFocus={(event) => {
                  event.target.select();
                }}
              />
            </Box>
            <ButtonGroup variant="outlined" aria-label="Upload token actions" sx={actionGroupSx}>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                loading={!hasConfiguredToken && savePending}
                aria-label={
                  hasConfiguredToken ? 'Regenerate Upload Token' : 'Generate Upload Token'
                }
                disabled={disabled || savePending}
                sx={[
                  actionButtonSx,
                  ...(!hasConfiguredToken
                    ? [
                        {
                          '&&': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                          },
                          '&&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      ]
                    : []),
                ]}
                onClick={onGenerateOrRegenerate}
              >
                <Tooltip
                  title={hasConfiguredToken ? 'Regenerate Upload Token' : 'Generate Upload Token'}
                  placement="top"
                >
                  <RotateRight sx={{ fontSize: 21 }} />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                loading={testPending}
                aria-label="Test Upload Token"
                disabled={disabled || !hasPlaintextToken || savePending || testPending}
                sx={[actionButtonSx, { minWidth: 56, width: 56, maxWidth: 56 }]}
                onClick={onTestUpload}
              >
                <Tooltip title="Test Upload Token" placement="top">
                  <PlayArrowIcon sx={{ fontSize: 22 }} />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                aria-label={getTargetActionLabel('copy', 'Upload Token')}
                disabled={disabled || !hasPlaintextToken || savePending}
                sx={actionButtonSx}
                onClick={copyUploadToken}
              >
                <Tooltip title={getTargetActionLabel('copy', 'Upload Token')} placement="top">
                  <CopyIcon />
                </Tooltip>
              </Button>
              <Button
                variant="ghost"
                fullWidthOnMobile={false}
                disableRipple
                aria-label={getTargetActionLabel('delete', 'Upload Token')}
                disabled={disabled || !hasConfiguredToken || savePending}
                sx={actionButtonSx}
                onClick={() => {
                  setRevokeOpen(true);
                }}
              >
                <Tooltip title={getTargetActionLabel('delete', 'Upload Token')} placement="top">
                  <DeleteIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </Button>
            </ButtonGroup>
          </Box>
          {hasPlaintextToken && !bannerDismissed ? (
            <StatusBanner
              tone="blue"
              variant="outlined"
              message="Copy the token now. It is only shown once."
              onClose={() => {
                setBannerDismissed(true);
              }}
              sx={{
                mt: s('sm'),
                mb: s('sm'),
                '& .MuiAlert-message': {
                  py: s('xs'),
                },
              }}
            />
          ) : null}
          {hasConfiguredToken ? (
            <Box sx={{ display: 'flex', gap: s('md'), width: '100%', alignItems: 'flex-end' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack gap="xxs">
                  <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                    Created
                  </Text>
                  <Text role="body" sx={settingsValueTextSx}>
                    {formatDate(activeTokens[0]?.createdAt ?? null, { includeTime: true }) || '-'}
                  </Text>
                </Stack>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack gap="xxs">
                  <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                    Last Used
                  </Text>
                  <Text role="body" sx={settingsValueTextSx}>
                    {formatDate(activeTokens[0]?.lastUsedAt ?? null, { includeTime: true }) || '-'}
                  </Text>
                </Stack>
              </Box>
              <Button
                variant="secondary"
                fullWidthOnMobile={false}
                onClick={() => {
                  setSnippetsOpen(true);
                }}
              >
                View Snippets
              </Button>
            </Box>
          ) : null}
        </Stack>
      </Stack>
      <DialogLayout
        open={snippetsOpen}
        onClose={() => {
          setSnippetsOpen(false);
        }}
        title="Upload Snippets"
        maxWidth="sm"
        headerGap="xs"
        contentOffset="xs"
        footerGap="md"
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              setSnippetsOpen(false);
            }}
          >
            Close
          </Button>
        }
        actionsAlign="flex-end"
      >
        <Stack gap="md">
          <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
            Use these snippets with your generated token to upload results files via cURL.
          </Text>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeSnippetTab}
              onChange={(_, next) => {
                setActiveSnippetTab(next as UploadSnippetFormat);
              }}
              aria-label="Upload snippet format tabs"
              sx={{
                minHeight: '30px !important',
                '& .MuiTabs-flexContainer': {
                  gap: s('lg'),
                },
                '& .MuiTab-root': {
                  fontSize: '15px',
                  minHeight: '30px !important',
                  height: '30px !important',
                  minWidth: 0,
                  paddingLeft: s('md'),
                  paddingRight: s('md'),
                },
              }}
            >
              <Tab label="XLSX" value="XLSX" sx={{ paddingBottom: s('xs') }} />
              <Tab label="CSV" value="CSV" sx={{ paddingBottom: s('xs') }} />
              <Tab label="XML" value="XML" sx={{ paddingBottom: s('xs') }} />
              <Tab label="JSON" value="JSON" sx={{ paddingBottom: s('xs') }} />
            </Tabs>
          </Box>
          <Box sx={{ mt: s('xs') }}>
            {activeSnippetTab === 'XLSX' ? (
              <CodeOutputBlock
                value={uploadXlsxSnippet}
                onCopy={() => {
                  copyUploadSnippet(uploadXlsxSnippet, 'XLSX');
                }}
                copyAriaLabel="Copy XLSX upload snippet"
                background="transparent"
                reserveCopySpace={false}
                sx={{ backgroundColor: 'background.default' }}
              />
            ) : null}
            {activeSnippetTab === 'CSV' ? (
              <CodeOutputBlock
                value={uploadCsvSnippet}
                onCopy={() => {
                  copyUploadSnippet(uploadCsvSnippet, 'CSV');
                }}
                copyAriaLabel="Copy CSV upload snippet"
                background="transparent"
                reserveCopySpace={false}
                sx={{ backgroundColor: 'background.default' }}
              />
            ) : null}
            {activeSnippetTab === 'XML' ? (
              <CodeOutputBlock
                value={uploadXmlSnippet}
                onCopy={() => {
                  copyUploadSnippet(uploadXmlSnippet, 'XML');
                }}
                copyAriaLabel="Copy XML upload snippet"
                background="transparent"
                reserveCopySpace={false}
                sx={{ backgroundColor: 'background.default' }}
              />
            ) : null}
            {activeSnippetTab === 'JSON' ? (
              <CodeOutputBlock
                value={uploadJsonSnippet}
                onCopy={() => {
                  copyUploadSnippet(uploadJsonSnippet, 'JSON');
                }}
                copyAriaLabel="Copy JSON upload snippet"
                background="transparent"
                reserveCopySpace={false}
                sx={{ backgroundColor: 'background.default' }}
              />
            ) : null}
          </Box>
        </Stack>
      </DialogLayout>
      <ConfirmDialog
        open={regenerateOpen}
        title="Regenerate Upload Token"
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Generate a new upload token for this project.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              This action revokes the existing upload token immediately.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Update your upload token after regenerating to avoid upload failures.
            </Text>
          </Stack>
        }
        confirmLabel="Regenerate Upload Token"
        confirmColor="primary"
        confirmVariant="contained"
        loading={savePending}
        onClose={() => {
          setRegenerateOpen(false);
        }}
        onConfirm={() => {
          void applyGenerateToken();
        }}
      />
      <ConfirmDialog
        open={revokeOpen}
        title={getDialogMessage('destructive.deleteNamed.title', { label: 'Upload Token' })}
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Delete this upload token to immediately block CI uploads using it.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              {getDialogMessage('destructive.cannotBeUndone')}
            </Text>
          </Stack>
        }
        confirmLabel={getDialogMessage('destructive.deleteNamed.confirmLabel', {
          label: 'Upload Token',
        })}
        confirmColor="error"
        loading={savePending}
        onClose={() => {
          setRevokeOpen(false);
        }}
        onConfirm={() => {
          void applyRevoke();
        }}
      />
    </Stack>
  );
  if (container === 'none') {
    return content;
  }
  return (
    <Panel title="Upload Token" padding="xl">
      {content}
    </Panel>
  );
};
